<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import Breadcrumbs from "../Breadcrumbs.vue";
import { activateDarkMode, deactivateDarkMode } from "@/assets/js/dark-mode";
import { subscribeToPush } from "@/services/pushService";

const store = useStore();
const route = useRoute();
const router = useRouter();

const showMenu = ref(false);      // Control campana
const showUserMenu = ref(false);  // Control usuario
const notificacionesActivas = ref(Notification.permission === 'granted');

const activarNotificaciones = async () => {
  const exito = await subscribeToPush();
  if (exito) {
    notificacionesActivas.value = true;
    alert("¡Notificaciones activadas correctamente!");
  } else {
    alert("Hubo un problema al activar las notificaciones, o el permiso fue denegado.");
  }
};

const usuario = computed(() => {
  const nombre = localStorage.getItem("first_name") || "Usuario";
  const apellido = localStorage.getItem("last_name") || "";
  const cargoStr = localStorage.getItem("cargo_nombre") || "Personal TAMEP";
  return {
    nombre_completo: `${nombre} ${apellido}`.trim(),
    cargo: cargoStr
  };
});

const isDarkMode = computed(() => store.state.darkMode);

const toggleTheme = () => {
  if (store.state.darkMode) {
    store.state.darkMode = false;
    store.commit("sidebarType", "bg-white");
    deactivateDarkMode();
  } else {
    store.state.darkMode = true;
    store.commit("sidebarType", "bg-default");
    activateDarkMode();
  }
};

const cerrarSesion = () => {
  localStorage.clear();
  router.push({ name: 'Signin' });
};

const currentRouteName = computed(() => route.name || "Inicio");
const currentDirectory = computed(() => {
  if (!route.path || route.path === "/" || route.path.includes("dashboard-default")) {
    return "Inicio";
  }
  return route.name || (route.path.split("/")[1] ? route.path.split("/")[1].charAt(0).toUpperCase() + route.path.split("/")[1].slice(1) : "Inicio");
});

const minimizeSidebar = () => store.commit("sidebarMinimize");
</script>


<template>
  <nav class="navbar navbar-main navbar-expand-lg px-0 mx-4 shadow-none border-radius-xl" v-bind="$attrs"
    id="navbarBlur" data-scroll="true">
    <div class="px-3 py-1 container-fluid">
      
      <div class="d-flex align-items-center">
        <breadcrumbs :current-page="currentRouteName" :current-directory="currentDirectory" />
      </div>

      <div class="collapse navbar-collapse mt-sm-0 me-md-0 me-sm-4 justify-content-end" id="navbar">
        <ul class="navbar-nav justify-content-end align-items-center">
          
          <li class="nav-item dropdown d-flex align-items-center me-3">
            <a href="#" class="px-0 nav-link font-weight-bold text-white d-flex align-items-center" 
               @click.prevent="showUserMenu = !showUserMenu; showMenu = false">
              <div class="d-flex flex-column text-end me-3 d-sm-block d-none">
                <span class="text-sm font-weight-bold text-white mb-0 text-uppercase">
                  {{ usuario.nombre_completo }}
                </span>
                <p class="text-xs text-white opacity-8 mb-0 text-uppercase" style="line-height: 1;">
                  {{ usuario.cargo }}
                </p>
              </div>
              <div class="avatar avatar-sm border-radius-lg shadow-sm" style="background-color: rgba(255, 255, 255, 0.2); backdrop-filter: blur(5px);">
                <i class="fas fa-user-tie text-white"></i>
              </div>
            </a>
            <ul class="px-2 py-3 dropdown-menu dropdown-menu-end me-sm-n4" :class="{ 'show': showUserMenu }">
              <li>
                <router-link class="dropdown-item border-radius-md" :to="{name: 'Profile'}">
                  <i class="fas fa-user me-2 text-dark"></i> Mi Perfil
                </router-link>
              </li>
              <li><hr class="horizontal dark"></li>
              <li>
                <a class="dropdown-item border-radius-md text-danger" href="javascript:;" @click="cerrarSesion">
                  <i class="fas fa-sign-out-alt me-2"></i> Cerrar Sesión
                </a>
              </li>
            </ul>
          </li>

          <li class="px-3 nav-item d-flex align-items-center">
            <a class="p-0 nav-link text-white cursor-pointer" @click="toggleTheme" style="font-size: 1.2rem; transition: transform 0.3s ease;" onmouseover="this.style.transform='scale(1.1)'" onmouseout="this.style.transform='scale(1)'">
              <i :class="isDarkMode ? 'fas fa-sun' : 'fas fa-moon'"></i>
            </a>
          </li>

          <li class="nav-item d-xl-none ps-3 d-flex align-items-center">
            <a href="#" @click.prevent="minimizeSidebar" class="p-0 nav-link hamburger-tamep">
              <div class="sidenav-toggler-inner">
                <i class="sidenav-toggler-line wrapper-line"></i>
                <i class="sidenav-toggler-line wrapper-line"></i>
                <i class="sidenav-toggler-line wrapper-line"></i>
              </div>
            </a>
          </li>

          <li class="nav-item dropdown d-flex align-items-center ps-2">
            <a href="#" class="p-0 nav-link text-white" @click.prevent="showMenu = !showMenu; showUserMenu = false">
              <i class="cursor-pointer fa fa-bell"></i>
            </a>
            <ul class="px-2 py-3 dropdown-menu dropdown-menu-end me-sm-n4" :class="{ 'show': showMenu }">
              <li class="mb-2">
                <a class="dropdown-item border-radius-md" href="javascript:;">
                  <div class="py-1 d-flex">
                    <div class="my-auto avatar avatar-sm bg-gradient-info me-3">
                      <i class="fas fa-tools"></i>
                    </div>
                    <div class="d-flex flex-column justify-content-center">
                      <h6 class="mb-1 text-sm font-weight-normal">Módulo de Servicios</h6>
                      <p class="mb-0 text-xs text-secondary">Proximamente mantenimientos</p>
                    </div>
                  </div>
                </a>
              </li>
              <li v-if="!notificacionesActivas"><hr class="horizontal dark"></li>
              <li v-if="!notificacionesActivas">
                <a class="dropdown-item border-radius-md text-info text-center" href="javascript:;" @click.prevent="activarNotificaciones">
                  <i class="fas fa-bell me-2"></i> Activar Notificaciones
                </a>
              </li>
            </ul>
          </li>

        </ul>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.navbar-main .text-white {
  color: #fff !important;
}

/* Dropdown Menu logic */
.dropdown-menu {
  display: none;
  position: absolute;
  z-index: 1100;
  transition: all 0.3s ease;
}

.dropdown-menu.show {
  display: block;
  /* Pequeño ajuste para que no aparezca tan pegado arriba */
  margin-top: 10px; 
}

/* Línea divisoria en el menú */
hr.horizontal {
  background-color: #e9ecef;
  height: 1px;
  margin: 0.5rem 0;
}

/* Ajuste para que la campana no se pegue al borde derecho */
.pe-2 {
  padding-right: 0.5rem !important;
}
</style>